import { modelLabel, useTranslation } from '@rhino-project/core/i18n';
import { Children, ReactNode, useCallback, useMemo, useState } from 'react';

import { Link, useLocation } from '@tanstack/react-router';
import { useModelIndexContext } from '@rhino-project/core/hooks';
import {
  useGlobalComponentForModel,
  useOverrides
} from '@rhino-project/core/hooks';
import { useBaseOwnerId } from '@rhino-project/core/hooks';
import { isBaseOwned } from '@rhino-project/core/utils';
import { IconButton } from '../buttons';
import { ModelCreateModal } from './ModelCreateModal';
import { ModelCreateModalActionSaveShow } from './ModelCreateModalActions';

interface ModelIndexActionProps {
  children?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  // parent is a related record whose id scopes the create; shape is dynamic.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parent?: any;
  [key: string]: unknown;
}

export const ModelIndexActionCreate = ({
  children,
  ...props
}: ModelIndexActionProps) => {
  const { t } = useTranslation();
  const { model, parentId: contextParentId } = useModelIndexContext();
  const baseOwnerId = useBaseOwnerId();
  const location = useLocation();

  const parentId = useMemo(() => {
    if (contextParentId) return contextParentId;

    if (isBaseOwned(model) && baseOwnerId) return baseOwnerId;

    return null;
  }, [model, contextParentId, baseOwnerId]);

  return (
    <IconButton
      as={Link}
      color="primary"
      icon="bi:plus"
      to={`${location.pathname}/new`}
      search={{ back: location.pathname, parentId }}
      {...props}
    >
      {children ||
        t('rhino.frontend.messages.add_value', { value1: modelLabel(model) })}
    </IconButton>
  );
};

const defaultModelComponents = {
  ModelCreateModalActionSave: ModelCreateModalActionSaveShow
};

export const ModelIndexActionCreateModal = ({
  overrides,
  children,
  ...props
}: ModelIndexActionProps) => {
  const { t } = useTranslation();
  const { parent } = props;
  const { model } = useModelIndexContext();

  const [modalOpen, setModalOpen] = useState(false);
  const baseOwnerId = useBaseOwnerId();
  const { ModelCreateModalActionSave } = useOverrides(
    defaultModelComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );

  const parentId = useMemo(() => {
    if (parent) return parent.id;

    if (isBaseOwned(model) && baseOwnerId) return baseOwnerId;

    return null;
  }, [model, parent, baseOwnerId]);

  const handleModalClose = () => setModalOpen(false);
  const handleClick = useCallback(() => setModalOpen(true), [setModalOpen]);

  return (
    <>
      <IconButton
        color="primary"
        icon="bi:plus"
        onPress={handleClick}
        {...props}
      >
        {children ||
          t('rhino.frontend.messages.add_value', { value1: modelLabel(model) })}
      </IconButton>
      <ModelCreateModal
        overrides={{
          ModelCreateModalActions: {
            ModelCreateModalActionSave
          }
        }}
        model={model}
        parentId={parentId}
        isOpen={modalOpen}
        onModalClose={handleModalClose}
      />
    </>
  );
};

const defaultComponents = {
  ModelIndexActionCreate
};

export interface ModelIndexActionsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  actions?: ReactNode;
  append?: boolean;
  prepend?: boolean;
  [key: string]: unknown;
}

export const ModelIndexActionsBase = ({
  overrides,
  actions,
  append = false,
  prepend = false
}: ModelIndexActionsProps) => {
  // eslint-disable-next-line @eslint-react/static-components
  const { ModelIndexActionCreate } = useOverrides(
    defaultComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );

  const computedDefaultActions = useMemo(
    // eslint-disable-next-line @eslint-react/no-missing-key, @eslint-react/static-components
    () => [<ModelIndexActionCreate />],
    // eslint-disable-next-line @eslint-react/exhaustive-deps, react-hooks/exhaustive-deps
    []
  );

  const computedActions = useMemo(() => {
    if (!actions) return computedDefaultActions;

    if (append)
      // eslint-disable-next-line @eslint-react/no-children-to-array
      return [...computedDefaultActions, ...Children.toArray(actions)];

    if (prepend)
      // eslint-disable-next-line @eslint-react/no-children-to-array
      return [...Children.toArray(actions), ...computedDefaultActions];

    return actions;
  }, [actions, append, prepend, computedDefaultActions]);

  return (
    <div className="flex flex-row flex-wrap justify-between">
      {
        // eslint-disable-next-line @eslint-react/no-children-map
        Children.map(computedActions, (action) => action)
      }
    </div>
  );
};

const MODAL_CREATE_OVERRIDES = {
  ModelIndexActionCreate: ModelIndexActionCreateModal
};
export const ModelIndexActionsModalCreate = (props: ModelIndexActionsProps) => (
  <ModelIndexActionsBase overrides={MODAL_CREATE_OVERRIDES} {...props} />
);

export const ModelIndexActions = (props: ModelIndexActionsProps) =>
  useGlobalComponentForModel('ModelIndexActions', ModelIndexActionsBase, props);
